import React from 'react';
import { Button } from '@/components/ui/button';
import { 
  MessageCircle, 
  Users, 
  Sparkles,
  Plus
} from 'lucide-react';

interface EmptyStateProps {
  type: 'no-chats' | 'no-conversation' | 'no-messages';
  onAction?: () => void;
  actionLabel?: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  type,
  onAction,
  actionLabel
}) => {
  const getEmptyStateContent = () => {
    switch (type) {
      case 'no-chats':
        return {
          icon: MessageCircle,
          title: "Nessuna chat",
          description: "Non hai ancora nessuna conversazione. Inizia una nuova chat per connetterti con altri utenti!",
          actionLabel: actionLabel || "Nuova Chat",
          gradient: "gradient-primary"
        };
      
      case 'no-conversation':
        return {
          icon: Users,
          title: "Seleziona una chat",
          description: "Scegli una conversazione dalla lista per iniziare a chattare e connetterti con la community AstroPet.",
          actionLabel: actionLabel || "Esplora Chat",
          gradient: "gradient-secondary"
        };
      
      case 'no-messages':
        return {
          icon: Sparkles,
          title: "Inizia la conversazione",
          description: "Rompi il ghiaccio! Invia il primo messaggio e inizia a chattare.",
          actionLabel: actionLabel || "Scrivi messaggio",
          gradient: "gradient-primary"
        };
      
      default:
        return {
          icon: MessageCircle,
          title: "Chat",
          description: "La tua esperienza chat inizia qui.",
          actionLabel: "Inizia",
          gradient: "gradient-primary"
        };
    }
  };

  const { icon: Icon, title, description, actionLabel: label, gradient } = getEmptyStateContent();

  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center max-w-md space-y-6">
        {/* Icon with Gradient Background */}
        <div className={`mx-auto w-24 h-24 rounded-3xl ${gradient} flex items-center justify-center shadow-lg shadow-primary/20`}>
          <Icon className="w-12 h-12 text-primary-foreground" />
        </div>

        {/* Content */}
        <div className="space-y-3">
          <h3 className="text-2xl font-bold gradient-text">
            {title}
          </h3>
          <p className="text-muted-foreground leading-relaxed">
            {description}
          </p>
        </div>

        {/* Action Button */}
        {onAction && (
          <Button
            onClick={onAction}
            className="gradient-primary shadow-primary/20 shadow-lg hover:shadow-primary/30 transition-smooth px-6 py-3 rounded-xl"
          >
            <Plus className="w-4 h-4 mr-2" />
            {label}
          </Button>
        )}

        {/* Decorative Elements */}
        <div className="flex justify-center space-x-2 mt-8">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full ${gradient} opacity-60`}
              style={{
                animationDelay: `${i * 0.2}s`,
                animation: 'pulse 2s infinite'
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default EmptyState;